import React, { useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Button from 'components/Button/Button';
import { cn } from 'components/lib/utils';

interface FilterControlProps {
  header: string;
  filterValue?: string;
  displayValue?: React.ReactNode;
  onClear: () => void;
  isLabelSearchBy?: boolean;
  children: (close: () => void) => React.ReactNode;
  disabled?: boolean;
}

const FilterControl: React.FC<FilterControlProps> = ({
  header,
  filterValue,
  displayValue,
  onClear,
  isLabelSearchBy = false,
  children,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const filterCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterCardRef.current &&
        !filterCardRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClearAnchor = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();
    onClear();
  };

  const close = () => setIsOpen(false);

  return (
    <div className="flex items-center gap-x-3">
      {isLabelSearchBy && (
        <div className="text-p-s text-base-foreground">Search by:</div>
      )}
      <div className="relative" ref={filterCardRef}>
        <div
          className={cn(
            'flex items-center gap-2 border border-dashed border-gray-300 rounded-lg px-4 py-2.5 bg-white focus-within:border-gray-400',
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {filterValue ? (
            <FontAwesomeIcon
              icon="fa-regular fa-circle-xmark"
              onClick={handleClearAnchor}
            />
          ) : (
            <FontAwesomeIcon icon="fa-regular fa-plus-circle" />
          )}
          <div className="text-gray-700 truncate text-p-s">
            <span className="font-medium">{header}</span>
            {filterValue && (
              <>
                <span className="mx-1 text-gray-300">|</span>
                <Button
                  variant="ghost"
                  className="h-6 text-p-s font-medium text-base-mutedForeground hover:bg-transparent"
                >
                  {displayValue}
                </Button>
              </>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-md mt-1 z-10">
            {children(close)}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterControl;
